import {
  StateMachineErrorCallback,
  Error as StateMachineErrorCode,
  StateMachineEventDef,
} from 'javascript-state-machine';

import {logger, padRight} from '../utils/Logger';
import {objectValues} from '../utils/objectUtils';
import {isNullish, tryJSONStringify} from '../utils/Utils';

/**
 * @deprecated use {@link whileIn} instead, to switch mindset and think about allowed events in the current state
 * instead of thinking of current event and its allowed states. Because as analytics we focus mostly on reporting
 * states or states changes.
 */
export const on = (event: string): {stayIn: (state: string) => StateMachineEventDef} => ({
  stayIn: (state: string) => ({name: event, from: state, to: state}),
});

type EventsToStateMachineEventDefFn = (...events: string[]) => StateMachineEventDef[];
type WhileInReturnType = {
  /**
   * Create {@link StateMachineEventDef} from event(s) to be used in the state machine definition, but ONLY to indicate
   * that we want to only pass event(s) over, to avoid a log message of ignored event in the current state from
   * the state machine.
   *
   * @remarks
   *
   * If we decide in the future to ignore {@link StateMachineErrorCode.INVALID_TRANSITION} logs created in
   * {@link customStateMachineErrorCallback}, this method and JSM event definitions created with could be removed
   * from source code. Until then, we use it to make code more readable.
   */
  passOver: EventsToStateMachineEventDefFn;

  /**
   * Same as {@link passOver}, but used in rare cases, when it indicates that we expect the event(s) to occur
   * in the current state, aka. we want to trigger the state machine event callbacks for these event(s),
   * even if we stay in the same state.
   *
   * @remarks
   *
   * If we don't expect the event while staying in the current state, we don't expect state machine event callbacks
   * to run, we should use {@link passOver} instead!
   */
  runEventCallbacksFor: EventsToStateMachineEventDefFn;
};

/**
 * This utility is here to help us write more readable code and let us define allowed/expected events in the current state.
 *
 * ### Example:
 *
 * Instead of writing:
 *
 * ```typescript
 * {name: Event.PAUSE, from: State.END, to: State.END},
 * {name: Event.SEEK, from: State.END, to: State.END},
 * {name: Event.SEEKED, from: State.END, to: State.END},
 * {name: Event.TIMECHANGED, from: State.END, to: State.END},
 * ```
 *
 * we can write:
 *
 * ```typescript
 * ...whileIn(State.END).passOver(Event.PAUSE, Event.SEEK, Event.SEEKED, Event.TIMECHANGED),
 * // OR when we expect and react on that event occurrence:
 * ...whileIn(State.END).runEventCallbacksFor(Event.PAUSE, Event.SEEK, Event.SEEKED, Event.TIMECHANGED),
 * ```
 *
 * @see customStateMachineErrorCallback
 * @see https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#handling-failures
 */
export const whileIn = (state: string): WhileInReturnType => {
  const stateMachineEventDefBuilderFn = (...events: string[]) =>
    events.map((event) => ({name: event, from: state, to: state}));
  return {runEventCallbacksFor: stateMachineEventDefBuilderFn, passOver: stateMachineEventDefBuilderFn};
};

/**
 * By default, if you try to call an event method not allowed in the current state,
 * the state machine will throw an exception (not allow it).
 *
 * This JSM library behavior can be changed by providing "a custom error handler" in the `create()`
 * method of the state machine, and we do that in every state machine!
 *
 * This way we make code more resilient to errors, because we do not throw errors from JSM event methods,
 * but instead we handle them here and just log them with {@link logger}.
 *
 * @see https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#handling-failures
 */
export const customStateMachineErrorCallback: StateMachineErrorCallback = (
  eventName,
  from,
  to,
  args,
  errorCode,
  errorMessage,
  _ex,
) => {
  const loggerMessage = `StateMachine ignored event "${eventName}", ${errorMessage}`;
  const loggerMessageData = {
    eventName,
    from,
    to,
    args: tryJSONStringify(args),
    errorCode,
    errorMessage,
  };

  // if caller tried to fire an event that was inappropriate in the current state
  if (errorCode === StateMachineErrorCode.INVALID_TRANSITION) {
    // The event itself is defined in JSM (in some other StateMachineEventDef). However, it is just not considered to
    // be appropriate in the current state.
    //
    // We should log it as a simple log. It is not an error nor a warning,
    // - because JSM will just stay in the same JSM state (and that can be considered as expected behavior)
    // - BUT it will NOT trigger JSM event callbacks like `onafterEVENT`, on `onbeforeEVENT`!
    logger.log(loggerMessage, loggerMessageData);
    return;
  }

  logger.error(loggerMessage, loggerMessageData);
};

/**
 * Log warning messages with {@link logger} about missing StateMachine callback,
 * if the state is not nullish and does not match any of the ignored states.
 */
export const logMissingCallbackWarning = (state: string | undefined, ignoreStates: string[] = []) => {
  if (isNullish(state)) {
    return;
  }

  const shouldReportMissingCallback = isNullish(ignoreStates.find((ignoredState) => state === ignoredState));
  if (shouldReportMissingCallback) {
    logger.warn('Could not find StateMachine callback function for ' + state);
  }
};

/** Log message with {@link logger} about StateMachine ENTER/LEAVE transition */
export const logStateMachineTransition = (
  transition: 'ENTER' | 'LEAVE',
  event: string | undefined,
  from: string | undefined,
  to: string | undefined,
) => {
  if (transition === 'ENTER') {
    logger.log(`[ENTER] ${padRight(to, 20)} EVENT: ${padRight(event, 20)} from ${padRight(from, 20)}`);
  } else {
    logger.log(`[LEAVE] ${padRight(from, 20)} EVENT: ${padRight(event, 20)} to ${padRight(to, 20)}`);
  }
};

/**
 * Return all values/states of the given state string enum.
 *
 * @param stateStringEnumObj
 *
 * @remarks
 *
 * We cannot directly check at if an object is a TypeScript string enum, because enums are erased at compile time and
 * become plain objects in JavaScript. However, you can heuristically check if an object looks like a string enum
 * by verifying that all its values are strings. This checks if all values of the object are strings, which is typical
 * for a string enum. However, this is not a foolproof check, just a heuristic.
 */
export function getAllStates<T extends {[key: string]: string}>(stateStringEnumObj: T): Array<T[keyof T]> {
  return objectValues(stateStringEnumObj) as Array<T[keyof T]>;
}

export function getAllStatesBut<T extends {[key: string]: string}>(
  excludedStates: Array<T[keyof T]>,
  stateEnumObj: T,
): Array<T[keyof T]> {
  return getAllStates(stateEnumObj).filter((s) => excludedStates.indexOf(s) < 0);
}
